import { TimeRange } from "../../../common/component/DateRangeSelector"
import { IFilterDto, IPagination, TFieldDefinition } from "../../../common/model/Core.model"
import { IAssetNestedDto } from "../../asset/Asset.model"
import { IClientNestedDto } from "../../client/Client.model"
import { IContactNestedDto } from "../../contact/Contact.model"
import { IContractNestedDto } from "../../contract/Contract.model"
import { IAttachment, IAttachmentStatus } from "../../media/Media.model"
import { ITaskDto } from "../task/Task.model"

export interface ITicketReference {
  id: string
  sequence: string
  createdAt: string
}

export interface IATicket {
  ticket: ITicketDto | undefined
  media: ITicketMediaDto[]
  tasks: ITaskDto[]
}

export interface ITicket {
  objectType: string
  createdBy: string
  createdAt: string
  updatedBy: string
  updatedAt: string
  organizationId: string
  client: IClientNestedDto
  contract?: IContractNestedDto
  asset?: IAssetNestedDto
  reportedAt: string
  reportedBy: IContactNestedDto
  id: string
  sequence: string
  statusCode: TicketStatus
  status: string
  startedAt?: string
  startingTime?: number
  impactCode: ImpactLevel
  impact: string
  priorityCode: PriorityLevel
  priority: string
  criticalityCode: CriticalityLevel
  criticality: string
  title: string
  description?: string
  notes?: string
  countryCode?: string
  country?: string
  address?: string
  latitude?: string
  longitude?: string
  resolution?: string
  resolvedAt?: string
  resolutionTime?: number
  closingNotes?: string
  closedAt?: string
  closingTime?: number
  parentTickets?: string[]
  totalAttachments?: number
  totalStorage?: number
  totalTasks?: number
}

export interface ITicketDto {
  createdAt: string
  updatedAt: string
  organizationId: string
  client: IClientNestedDto
  contract?: IContractNestedDto
  asset?: IAssetNestedDto
  reportedAt: string
  reportedBy: IContactNestedDto
  id: string
  sequence: string
  statusCode: TicketStatus
  status: string
  startedAt?: string
  startingTime?: number
  impactCode: ImpactLevel
  impact: string
  priorityCode: PriorityLevel
  priority: string
  criticalityCode: CriticalityLevel
  criticality: string
  title: string
  description?: string
  notes?: string
  countryCode?: string
  country?: string
  address?: string
  latitude?: string
  longitude?: string
  resolution?: string
  resolvedAt?: string
  resolutionTime?: number
  closingNotes?: string
  closedAt?: string
  closingTime?: number
  parentTickets?: string[]
  totalAttachments?: number
  totalStorage?: number
  totalTasks?: number
}

export interface ITicketCreateRequestDto {
  createdBy?: string
  createdAt?: string
  organizationId?: string
  clientId: string
  assetId?: string
  reportedAt: string
  reportedBy: string
  statusCode: string
  status: string
  impactCode: string
  impact: string
  priorityCode: string
  priority: string
  criticalityCode: string
  criticality: string
  title: string
  description?: string
  notes?: string
  countryCode?: string
  country?: string
  address?: string
  latitude?: string
  longitude?: string
  parentTickets?: string[]
  attachments?: IAttachment[]
}

export interface ITicketUpdateRequestDto {
  updatedBy?: string
  updatedAt?: string
  organizationId?: string
  clientId: string
  assetId?: string
  id?: string
  statusCode: string
  status: string
  reportedAt: string
  reportedBy: string
  impactCode: string
  impact: string
  priorityCode: string
  priority: string
  criticalityCode: string
  criticality: string
  title: string
  description?: string
  notes?: string
  countryCode?: string
  country?: string
  address?: string
  latitude?: string
  longitude?: string
  resolution?: string
  closingNotes?: string
  parentTickets?: string[]
  attachments?: IAttachment[]
}

export interface ITicketSearchRequestDto {
  pagination?: IPagination
  fields?: TFieldDefinition
  filters?: IFilterDto[]
  timeRange?: TimeRange
}

export interface ITicketMediaDto {
  createdAt: string
  updatedAt: string
  organizationId: string
  ticketId: string
  id: string
  fileId: string
  fileName: string
  temporalPath: string
  path: string
  size: number
  contentType: string
  comments?: string
  status: IAttachmentStatus
  url: string
}

export interface ITicketMediaDeleteRequestDto {
  ticketId: string
  id: string
}

export enum ITicketFilter {
  CLIENT_HASH = 'CLIENT_HASH',
  CLIENT_ID = 'CLIENT_ID',
  CLIENT = 'CLIENT',
  CONTRACT_ID = 'CONTRACT_ID',
  ASSET_ID = 'ASSET_ID',
  ASSET_GUI = 'ASSET_GUI',
  FAMILY_CODE = 'FAMILY_CODE',
  FAMILY = 'FAMILY',
  AREA_CODE = 'AREA_CODE',
  AREA = 'AREA',
  BRAND = 'BRAND',
  MODEL = 'MODEL',
  SERIAL = 'SERIAL',
  ID = 'ID',
  STATUS = 'STATUS',
  CRITICALITY = 'CRITICALITY',
  TITLE = 'TITLE',
  ADDRESS = 'ADDRESS',
}

export enum PriorityLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}
export const PriorityLevelOptions = [
  PriorityLevel.LOW,
  PriorityLevel.MEDIUM,
  PriorityLevel.HIGH,
  PriorityLevel.URGENT,
]

export enum ImpactLevel {
  MINOR = 'MINOR',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  MAJOR = 'MAJOR',
}
export const ImpactLevelOptions = [
  ImpactLevel.MINOR,
  ImpactLevel.MEDIUM,
  ImpactLevel.HIGH,
  ImpactLevel.MAJOR,
]

export enum CriticalityLevel {
  LOW = 'C4', // Low level of severity - GREEN
  MODERATE = 'C3', // Medium level of severity - YELLOW
  HIGH = 'C2', // High level of severity - ORANGE
  SEVERE = 'C1', // Highest level of severity - RED
}

export enum TicketStatus {
  NEW = 'NEW',
  IN_PROGRESS = 'IN_PROGRESS',
  ON_HOLD = 'ON_HOLD',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
}

export const getStatusTypes = (status: TicketStatus): TicketStatus[] => {
  switch (status) {
    case TicketStatus.NEW:
      return [TicketStatus.IN_PROGRESS, TicketStatus.ON_HOLD]
    case TicketStatus.IN_PROGRESS:
      return [TicketStatus.ON_HOLD, TicketStatus.RESOLVED]
    case TicketStatus.ON_HOLD:
      return [TicketStatus.IN_PROGRESS, TicketStatus.RESOLVED]
    case TicketStatus.RESOLVED:
      return [TicketStatus.IN_PROGRESS, TicketStatus.ON_HOLD, TicketStatus.CLOSED]
    case TicketStatus.CLOSED:
      return [TicketStatus.IN_PROGRESS, TicketStatus.ON_HOLD]
    default:
      return [TicketStatus.IN_PROGRESS, TicketStatus.ON_HOLD]
  }
}

export const getCriticality = (impact: string, urgency: string): CriticalityLevel => {
  if (impact === ImpactLevel.MAJOR && urgency === PriorityLevel.URGENT) {
    return CriticalityLevel.SEVERE
  }
  if (impact === ImpactLevel.MAJOR && urgency === PriorityLevel.HIGH) {
    return CriticalityLevel.SEVERE
  }
  if (impact === ImpactLevel.MAJOR && urgency === PriorityLevel.MEDIUM) {
    return CriticalityLevel.HIGH
  }
  if (impact === ImpactLevel.MAJOR && urgency === PriorityLevel.LOW) {
    return CriticalityLevel.MODERATE
  }

  if (impact === ImpactLevel.HIGH && urgency === PriorityLevel.URGENT) {
    return CriticalityLevel.SEVERE
  }
  if (impact === ImpactLevel.HIGH && urgency === PriorityLevel.HIGH) {
    return CriticalityLevel.HIGH
  }
  if (impact === ImpactLevel.HIGH && urgency === PriorityLevel.MEDIUM) {
    return CriticalityLevel.MODERATE
  }
  if (impact === ImpactLevel.HIGH && urgency === PriorityLevel.LOW) {
    return CriticalityLevel.MODERATE
  }

  if (impact === ImpactLevel.MEDIUM && urgency === PriorityLevel.URGENT) {
    return CriticalityLevel.HIGH
  }
  if (impact === ImpactLevel.MEDIUM && urgency === PriorityLevel.HIGH) {
    return CriticalityLevel.MODERATE
  }
  if (impact === ImpactLevel.MEDIUM && urgency === PriorityLevel.MEDIUM) {
    return CriticalityLevel.MODERATE
  }
  if (impact === ImpactLevel.MEDIUM && urgency === PriorityLevel.LOW) {
    return CriticalityLevel.LOW
  }

  if (impact === ImpactLevel.MINOR && urgency === PriorityLevel.URGENT) {
    return CriticalityLevel.MODERATE
  }
  if (impact === ImpactLevel.MINOR && urgency === PriorityLevel.HIGH) {
    return CriticalityLevel.MODERATE
  }
  if (impact === ImpactLevel.MINOR && urgency === PriorityLevel.MEDIUM) {
    return CriticalityLevel.LOW
  }
  if (impact === ImpactLevel.MINOR && urgency === PriorityLevel.LOW) {
    return CriticalityLevel.LOW
  }
  return CriticalityLevel.LOW
}

export const mapCriticalityLabelColor = (criticality: string) => {
  let color = 'fs-6 ms-3 me-2 ';
  switch (criticality) {
    case CriticalityLevel.LOW:
      color += 'text-primary';
      break;
    case CriticalityLevel.MODERATE:
      color += 'text-success';
      break;
    case CriticalityLevel.HIGH:
      color += 'text-warning';
      break;
    case CriticalityLevel.SEVERE:
      color += 'text-danger';
      break;
    default:
      color += 'text-primary';
  }
  return color;
}